import React, { useState } from "react";
import {
  Button,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import AddIcon from "@mui/icons-material/Add";
import SearchBar from "../../components/SearchBar";
import SwitchToggle from "../../components/SwitchToggle";
import AddFeatureGroup from "../../Modals/addFeatureGroup";
import { useGetFeatureGroupsQuery, useGetCategoriesQuery, useUpdateFeatureGroupMutation, useDeleteFeatureGroupMutation } from "../../service/features/api";
import { usePermission } from "../../hooks/usePermission";
import Can from "../../components/Can";
import Pagination from "../../components/Panigation";
import { Group_Type, Field_Type, VEHICLE_TYPE } from "../../constants/enum";
import DeleteConfirmModal from "../../Modals/DeleteConfirmModal";
import { toast } from "react-toastify";
import { getVehicleTypeLabel } from "../../utils/vehicle";

const ManageFeatures: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("Vehicle Specification", "isEdit");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [category, setCategory] = React.useState(''); // default empty for all
  const [groupType, setGroupType] = React.useState(''); // default empty for all
  const [vehicleType, setVehicleType] = useState<string>('');
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedFeature, setSelectedFeature] = useState<any>(null);

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setCategory(event.target.value as string);
    setPage(1);
  };

  const handleGroupTypeChange = (event: SelectChangeEvent) => {
    setGroupType(event.target.value as string);
    setPage(1);
  };

  const handleVehicleTypeChange = (event: SelectChangeEvent) => {
    setVehicleType(event.target.value as string);
    setCategory('');
    setPage(1);
  };

  // Queries
  const { data: categoriesData } = useGetCategoriesQuery({ 
    page: 1, 
    limit: 100,
    type: vehicleType || undefined,
  });
  const { data: featuresData, isLoading, isFetching } = useGetFeatureGroupsQuery({
    page,
    limit,
    search: searchTerm,
    categoryId: category || undefined,
    groupType: groupType || undefined,
    type: vehicleType || undefined,
  });

  const [updateFeatureGroup] = useUpdateFeatureGroupMutation();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  const [deleteFeatureGroup, { isLoading: isDeleting }] = useDeleteFeatureGroupMutation();

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      try {
        await deleteFeatureGroup(itemToDelete).unwrap();
        toast.success("Feature Group deleted successfully / تم حذف مجموعة الميزات بنجاح");
        setDeleteConfirmOpen(false);
        setItemToDelete(null);
      } catch (err: any) {
        toast.error(err?.data?.message || "Failed to delete / فشل الحذف");
      }
    }
  };

  const features = featuresData?.data?.data || [];
  const paginator = featuresData?.data?.paginator || {};

  const getGroupTypeName = (value: number) => {
    const entry = Object.entries(Group_Type).find(([_, val]) => val === value);
    if (!entry) return "-";
    // Convert INTERIOR to Interior, COMFORT_CONVENIENCE to Comfort Convenience
    return entry[0].split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };

  const getFieldTypeName = (value: number) => {
    // Iterate over Field_Type keys to find the match
    const key = Object.keys(Field_Type).find(k => Field_Type[k as keyof typeof Field_Type] === value);
    return key ? key.charAt(0) + key.slice(1).toLowerCase() : "-";
  };

  const handleToggleStatus = async (feature: any) => {
    try {
      const payload = {
        id: feature._id,
        name: feature.name,
        groupType: feature.groupType,
        fieldType: feature.fieldType,
        type: feature.type || 1,
        categoryId: Array.isArray(feature.categories) ? feature.categories.map((c: any) => c._id) : (feature.categoryId || []),
        isActive: !feature.isActive
      };
      const res = await updateFeatureGroup(payload).unwrap();
      toast.success(res?.message || "Status updated successfully / تم تحديث الحالة بنجاح");
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to update status / فشل تحديث الحالة");
      console.error("Failed to update status", error);
    }
  };

  const handleDeleteClick = (id: string) => {
    setItemToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleView = (id: string) => {
    navigate(`/manage-features/${id}`);
  };

  return (
    <>
      <CommonBreadcrumb
        title="Manage Features / إدارة الميزات"
        breadcrumbItems={[
          { label: "Dashboard / لوحة المعلومات", onClick: () => navigate("/dashboard") },
          { label: "Manage Features / إدارة الميزات" },
        ]}
      />

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={(term) => {
                setSearchTerm(term);
                setPage(1);
              }}
              placeholder="Search Groups... / ابحث عن المجموعات..."
            />
            <div className="form">
              <div className="control_group">
                <Select
                  fullWidth
                  value={vehicleType}
                  onChange={handleVehicleTypeChange}
                  displayEmpty
                >
                  <MenuItem value="">Select Vehicle Type / اختر نوع المركبة</MenuItem>
                  {Object.values(VEHICLE_TYPE)
                    .filter((v) => typeof v === "number")
                    .map((type) => (
                      <MenuItem key={type} value={type.toString()}>
                        {getVehicleTypeLabel(type as number)}
                      </MenuItem>
                    ))}
                </Select>
              </div>
              <div className="control_group">
                <Select
                  fullWidth
                  value={category}
                  onChange={handleCategoryChange}
                  displayEmpty
                >
                  <MenuItem value="">Select Category / اختر الفئة</MenuItem>
                  {categoriesData?.data?.data?.map((cat: any) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <div className="control_group">
                <Select
                  fullWidth
                  value={groupType}
                  onChange={handleGroupTypeChange}
                  displayEmpty
                >
                  <MenuItem value="">Group Type / نوع المجموعة</MenuItem>
                  {Object.entries(Group_Type).map(([key, value]) => (
                    <MenuItem key={value} value={value.toString()}>
                      {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>
          </div>

          <div className="right_s">
            <Can module="Vehicle Specification" action="isAdd" isDisabledMode={true}>
              <Button className="btn btn_primary" onClick={() => {
                setSelectedFeature(null);
                setOpenModal(true);
              }}>
                <AddIcon /> Add Feature / إضافة ميزة
              </Button>
            </Can>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sr. No. / الرقم التسلسلي</TableCell>
                <TableCell>Feature Group Name / اسم مجموعة الميزات</TableCell>
                <TableCell>Group Type / نوع المجموعة</TableCell>
                <TableCell>Field Type / نوع الحقل</TableCell>
                <TableCell>Categories / الفئات</TableCell>
                <TableCell>Sub-Features / الميزات الفرعية</TableCell>
                <TableCell>Disable/Enable / تعطيل/تفعيل</TableCell>
                <TableCell>Actions / الإجراءات</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading || isFetching ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ padding: '2rem' }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : features.length > 0 ? (
                features.map((row: any, index: number) => (
                  <TableRow hover key={row._id}>
                    <TableCell>{(paginator.currentPage - 1) * paginator.perPage + index + 1}</TableCell>

                    <TableCell>
                      <div className="user_block click_action" onClick={() => handleView(row._id)}>
                        <p><b>{row.name}</b></p>
                      </div>
                    </TableCell>

                    <TableCell>{getGroupTypeName(row.groupType)}</TableCell>

                    <TableCell>{row.fieldType ? getFieldTypeName(row.fieldType) : 'Checkbox / خانة اختيار'}</TableCell>

                    <TableCell>
                      {row.categories?.map((c: any) => c.name).join(", ") || '-'}
                    </TableCell>

                    <TableCell>
                      <Button
                        className="btn btn_primary sm"
                        onClick={() => handleView(row._id)}
                      >
                        View / عرض ({row.options?.length || 0})
                      </Button>
                    </TableCell>

                    <TableCell>
                      {canEdit ? (
                        <SwitchToggle
                          checked={row.isActive}
                          onChange={() => handleToggleStatus(row)}
                        />
                      ) : (
                        <span className={`status_btn ${row.isActive ? "c_dark_info" : "c_dark_warning"}`}>
                          {row.isActive ? "Enabled / مفعل" : "Disabled / معطل"}
                        </span>
                      )}
                    </TableCell>

                    <TableCell>
                      <div className="table_actions v2">
                        <Can module="Vehicle Specification" action="isEdit" isDisabledMode={true}>
                          <Tooltip title="Edit / تعديل" arrow>
                            <IconButton onClick={() => {
                              setSelectedFeature(row);
                              setOpenModal(true);
                            }}>
                              <img src="/static/images/edit2_icon.svg" alt="Edit / تعديل" />
                            </IconButton>
                          </Tooltip>
                        </Can>
                        <Can module="Vehicle Specification" action="isDelete" isDisabledMode={true}>
                          <Tooltip title="Delete / حذف" arrow>
                            <IconButton onClick={() => handleDeleteClick(row._id)}>
                              <img src="/static/images/trash2_icon.svg" alt="Delete / حذف" />
                            </IconButton>
                          </Tooltip>
                        </Can>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No features found / لا توجد ميزات
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {paginator && paginator.pageCount > 1 && (
          <Pagination
            module={features}
            page={page}
            onPageChange={(p) => setPage(p)}
            totalPages={paginator.pageCount}
            totalCount={paginator.itemCount}
            setPage={setPage}
          />
        )}
      </div>

      <AddFeatureGroup
        open={openModal}
        setOpen={setOpenModal}
        featureGroupData={selectedFeature}
      />

      <DeleteConfirmModal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Feature Group / حذف مجموعة الميزات"
        message="Are you sure you want to delete this feature group? / هل أنت متأكد أنك تريد حذف مجموعة الميزات هذه؟"
        isLoading={isDeleting}
      />
    </>
  );
};

export default ManageFeatures;